import { ActionError, Button, ChoiceRow, Radio, RadioGroup, cn } from '@westhetics/ui'
import { useEffect, useId, useState } from 'react'
import { FieldNote, publicField, publicTextAction } from '@/app/public-screen'
import { noteText, supportText } from '@/app/shell'
import { errorMessage, fieldErrors } from '@/lib/errors'
import type { CodeSent, EmailOption } from './api'

/**
 * Escolher o e-mail, receber o código e confirmar (RN-PAC-52, RN-POR-19): o mesmo painel em Entrar e
 * no cadastro pelo link. Os e-mails vêm mascarados; com um só, ele já vem escolhido. Os botões são
 * `type="button"`: o painel vive dentro do formulário da tela e não o envia.
 */

/** "Enviar outro código" libera 60 segundos depois do último pedido (o limite da API por e-mail). */
export const RESEND_AFTER_SECONDS = 60

const CODE_LENGTH_MESSAGE = 'O código tem 6 dígitos. Confira o e-mail e digite todos.'

function useSecondsUntil(deadline: number | null) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (deadline === null) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [deadline])
  if (deadline === null) return 0
  return Math.min(RESEND_AFTER_SECONDS, Math.max(0, Math.ceil((deadline - now) / 1000)))
}

export function CodePanel({
  options,
  onSend,
  onConfirm,
  confirmLabel,
  confirmingLabel,
  className,
}: {
  options: EmailOption[]
  onSend: (emailId: string) => Promise<CodeSent>
  /** Rejeita com o erro da API: o painel mostra o do campo ou a caixa de erro. */
  onConfirm: (emailId: string, code: string) => Promise<void>
  confirmLabel: string
  confirmingLabel: string
  className?: string
}) {
  const id = useId()
  const [emailId, setEmailId] = useState(options[0]?.id ?? '')
  const [sent, setSent] = useState<{ message: string; at: number } | null>(null)
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState<unknown>(null)
  const [code, setCode] = useState('')
  const [codeTouched, setCodeTouched] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [confirmError, setConfirmError] = useState<unknown>(null)
  const secondsToResend = useSecondsUntil(sent ? sent.at + RESEND_AFTER_SECONDS * 1000 : null)
  const chosen = options.find((option) => option.id === emailId)

  async function send() {
    if (sending || !emailId) return
    setSending(true)
    setSendError(null)
    try {
      const result = await onSend(emailId)
      setSent({ message: result.message, at: Date.now() })
      setCode('')
      setCodeTouched(false)
      setConfirmError(null)
    } catch (error) {
      setSendError(error)
    } finally {
      setSending(false)
    }
  }

  async function confirm() {
    setCodeTouched(true)
    if (code.length !== 6 || confirming) return
    setConfirming(true)
    setConfirmError(null)
    try {
      await onConfirm(emailId, code)
    } catch (error) {
      setConfirmError(error)
    } finally {
      setConfirming(false)
    }
  }

  const sendFieldError = fieldErrors(sendError).emailId
  const sendBoxError = sendError && !sendFieldError ? errorMessage(sendError) : null
  const codeError =
    fieldErrors(confirmError).code ??
    (codeTouched && code.length !== 6 ? CODE_LENGTH_MESSAGE : null)
  const confirmBoxError =
    confirmError && !fieldErrors(confirmError).code ? errorMessage(confirmError) : null

  if (!sent) {
    return (
      <div className={className}>
        {options.length > 1 ? (
          <>
            <p className={cn(supportText, 'mt-1')}>Escolha o e-mail para receber o código.</p>
            <RadioGroup
              value={emailId}
              onValueChange={setEmailId}
              aria-label="E-mail para receber o código"
              className="mt-4 flex flex-col gap-2"
            >
              {options.map((option) => (
                <ChoiceRow
                  key={option.id}
                  htmlFor={`${id}-${option.id}`}
                  control={<Radio id={`${id}-${option.id}`} value={option.id} />}
                >
                  <span className="break-all">{option.masked}</span>
                </ChoiceRow>
              ))}
            </RadioGroup>
          </>
        ) : (
          <p className={cn(supportText, 'mt-1 break-words')}>
            O código vai para <span className="text-ink">{chosen?.masked}</span>.
          </p>
        )}
        <FieldNote id={`${id}-email-note`} error={sendFieldError ?? null} />
        {sendBoxError ? (
          <ActionError
            className="mt-6"
            title="Não foi possível enviar o código"
            description={sendBoxError}
          />
        ) : null}
        <Button
          type="button"
          size="lg"
          className="mt-6"
          loading={sending}
          loadingLabel="Enviando…"
          onClick={() => void send()}
        >
          Enviar código
        </Button>
      </div>
    )
  }

  return (
    <div className={className}>
      <p className={cn(supportText, 'mt-1')}>{sent.message}</p>
      <p className={cn(noteText, 'mt-2 break-words')}>
        E-mail escolhido: <span className="text-ink">{chosen?.masked}</span>
      </p>
      <label htmlFor={`${id}-code`} className="eyebrow mt-7 block">
        Código
      </label>
      <input
        id={`${id}-code`}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        value={code}
        aria-invalid={codeError ? true : undefined}
        aria-describedby={codeError ? `${id}-code-note` : undefined}
        onChange={(event) => {
          // O e-mail mostra "482 913": o corte vem depois de tirar o que não é dígito.
          setCode(event.target.value.replace(/\D/g, '').slice(0, 6))
          setConfirmError(null)
        }}
        onBlur={() => setCodeTouched(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            void confirm()
          }
        }}
        className={cn(publicField, 'font-display text-[18px] tracking-[.3em]')}
      />
      <FieldNote id={`${id}-code-note`} error={codeError} />
      {confirmBoxError ? (
        <ActionError
          className="mt-6"
          title="Não foi possível confirmar"
          description={confirmBoxError}
        />
      ) : null}
      {sendBoxError ? (
        <ActionError
          className="mt-6"
          title="Não foi possível enviar outro código"
          description={sendBoxError}
        />
      ) : null}
      <Button
        type="button"
        size="lg"
        className="mt-7"
        loading={confirming}
        loadingLabel={confirmingLabel}
        onClick={() => void confirm()}
      >
        {confirmLabel}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="mt-3"
        disabled={secondsToResend > 0}
        loading={sending}
        loadingLabel="Enviando…"
        onClick={() => void send()}
      >
        {secondsToResend > 0
          ? `Enviar outro código em ${secondsToResend} s`
          : 'Enviar outro código'}
      </Button>
      {options.length > 1 ? (
        <button
          type="button"
          onClick={() => {
            setSent(null)
            setSendError(null)
            setConfirmError(null)
          }}
          className={cn(publicTextAction, 'mt-6')}
        >
          Usar outro e-mail
        </button>
      ) : null}
    </div>
  )
}
